import { useAutoAnimate } from "@formkit/auto-animate/react";
import { StackIcon, WarningIcon } from "@xola/icons";
import clsx from "clsx";
import { isEmpty } from "lodash";
import React from "react";
import { useHistory } from "react-router-dom";
import { Merchandise, ProductObject } from "types";
import { Currency } from "@/components/Common/Currency";
import { NoResults } from "@/components/Common/NoResults";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { ProductImage } from "@/components/Common/ProductImage";
import { invalidateArrivalCache } from "@/components/DatePicker/useArrivalTimeslots";
import { ProductBadge } from "@/components/PurchaseCreate/Membership/MembershipList";
import {
    getLowestMerchandisePrice,
    getMerchandisePurchaseAvailability,
    getValidMerchandises,
    hasMerchandisePricing,
    hasVariantsEnabled,
} from "@/models/Merchandise";
import { CreatePurchaseStep, useCreatePurchaseStore } from "@/stores/createPurchaseStore";
import { getSeller } from "@/utils/getSeller";
import { sortBy } from "@/utils/locale";

interface MerchandiseListProps {
    merchandises: Merchandise[];
}

export const MerchandiseList = ({ merchandises }: MerchandiseListProps) => {
    const [animationParent] = useAutoAnimate<HTMLDivElement>();
    const validMerchandises = getValidMerchandises(merchandises).filter(
        (m) => !(hasVariantsEnabled(m) && !hasMerchandisePricing(m)),
    );
    const sortedMerchandises = sortBy(validMerchandises, "name", false);

    return (
        <div className="px-0 py-10">
            <div className="text-lg font-bold">Merchandise</div>
            {sortedMerchandises.length === 0 ? (
                <NoResults>No results found</NoResults>
            ) : (
                <div
                    ref={animationParent}
                    className="mt-10 grid grid-cols-3 gap-y-5 gap-x-2"
                    data-testid="merchandise-list"
                >
                    {sortedMerchandises.map((merchandise) => (
                        <MerchandiseListItem key={merchandise.id} merchandise={merchandise} />
                    ))}
                </div>
            )}
        </div>
    );
};

interface MerchandiseListItemProps {
    merchandise: Merchandise;
}

const MerchandiseListItem = ({ merchandise }: MerchandiseListItemProps) => {
    const history = useHistory<{ isAddingItem?: boolean } | null>();
    const seller = getSeller();
    const isAddingItem = history.location.state?.isAddingItem === true;
    const { resetState, initializeMerchandiseItem, setCurrency } = useCreatePurchaseStore.getState().actions;

    const hasSelectedOrganizer = useCreatePurchaseStore((state) => !!state.organizer);
    const hasAnyItems = useCreatePurchaseStore((state) => !isEmpty(state.purchase.items));
    const { isUnavailable, isOutOfStock } = getMerchandisePurchaseAvailability(merchandise);
    const isDisabled = isUnavailable;

    const handleMerchandiseSelect = () => {
        if (isDisabled) {
            return;
        }

        if (!isAddingItem && (!hasSelectedOrganizer || hasAnyItems)) {
            resetState();
            invalidateArrivalCache();
        }

        initializeMerchandiseItem(merchandise);
        setCurrency(seller.currency);
        useCreatePurchaseStore.setState({
            step: CreatePurchaseStep.SELECT_DATE,
            productObject: ProductObject.WARE,
        });
        history.push("/cart");
    };

    return (
        <MerchandiseTile
            merchandise={merchandise}
            isDisabled={isDisabled}
            isUnavailable={isUnavailable}
            isOutOfStock={isOutOfStock}
            onClick={handleMerchandiseSelect}
        />
    );
};

interface MerchandiseTileProps {
    merchandise: Merchandise;
    isDisabled: boolean;
    isUnavailable: boolean;
    isOutOfStock: boolean;
    onClick: () => void;
}

const MerchandiseTile = ({ merchandise, isDisabled, isUnavailable, isOutOfStock, onClick }: MerchandiseTileProps) => {
    const price = getLowestMerchandisePrice(merchandise);

    return (
        <div
            className={clsx("flex flex-row rounded py-2", {
                "cursor-pointer hover:bg-secondary-hover": !isDisabled,
                "cursor-not-allowed opacity-60": isDisabled,
            })}
            data-testid="merchandise-list-item"
            onClick={isDisabled ? undefined : onClick}
        >
            <ProductImage product={merchandise} />
            <div className="flex flex-col space-y-1">
                <NoTranslate className="text-md font-bold">{merchandise.name}</NoTranslate>
                <div className="flex flex-wrap gap-2">
                    {isOutOfStock && <ProductBadge color="danger" text="Out of Stock" icon={<WarningIcon />} />}
                    {isUnavailable && <ProductBadge color="danger" text="Unavailable" icon={<WarningIcon />} />}
                    {price !== null && (
                        <ProductBadge
                            text={
                                <>
                                    {"From\u00A0"}
                                    <Currency>{price}</Currency>
                                </>
                            }
                            icon={<StackIcon />}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
